// Base-path support so the whole app can be served under a URL prefix (e.g.
// "/v2") and run side-by-side with the legacy app at "/". The prefix is baked in
// at build time by Vite's `base` config (see vite.config.ts, driven by
// PH_BASE_PATH) and exposed as import.meta.env.BASE_URL — everything here derives
// from that single value, so nothing hardcodes "/v2".
//
// Router-based navigation (<Link>, useNavigate) is already base-aware via the
// router basename set in the entry files, so it needs no changes. Only URLs we
// build BY HAND that must live on this app — raw <a href>, window.location
// assignments, and callback URLs we hand to the backend (Stripe/Cognito return
// paths) — have to go through withBase()/originUrl() to keep the "/v2" prefix.
//
// EXCEPTION — the admin. The new admin is the ONLY admin now and is served at the
// root "/admin" on every host (apex, tenant subdomains, custom domains) by the
// base="/" build. "/v2/admin" is retired: the /v2 build's server redirects it to
// "/admin" and <AdminBaseEscape> does the same for in-app navigation. So admin
// paths are NEVER prefixed — every admin link we build is the canonical "/admin…".

/** "/v2/" when built with a prefix, otherwise "/". Always has a trailing slash. */
export const BASE_URL: string = import.meta.env.BASE_URL || "/";

/** React Router basename: "/v2" (no trailing slash), or "/" at the root. */
export const ROUTER_BASENAME: string = BASE_URL.replace(/\/+$/, "") || "/";

/** True for the app-relative admin paths ("/admin", "/admin/…", "/admin?…"). */
export const isAdminPath = (path: string): boolean =>
  path === "/admin" || /^\/admin[/?#]/.test(path);

/**
 * Prefix an app-absolute path with the base: withBase("/login") → "/v2/login".
 * Admin paths are always served at the root, so withBase("/admin/x") → "/admin/x".
 */
export const withBase = (path: string): string => {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (BASE_URL === "/" || isAdminPath(p)) return p;
  return ROUTER_BASENAME + p;
};

/**
 * Absolute URL on the current origin, base-aware (admin paths stay at the root):
 * originUrl("/register") → "https://host/v2/register",
 * originUrl("/admin/workspaces/new") → "https://host/admin/workspaces/new".
 * Falls back to a relative base-aware path during SSR (no window).
 */
export const originUrl = (path: string): string =>
  (typeof window !== "undefined" ? window.location.origin : "") + withBase(path);
